import { mountReactRoot } from './bootstrap/mountReactRoot';
import './assets/global.css';
import { CartSideWidget } from './components/cart/CartSideWidget';
import { cartService } from './services/cartService'; // 确保路径正确
import { requestCartRefresh } from './bootstrap/refreshCoordinator';
import { useCartStore } from './store/useCartStore';
import { CartEventBridge } from './events/cartEventBridge';
import { initNativeInterceptor } from './bootstrap/nativeInterceptor';
import { initCartRefreshTriggers } from './bootstrap/cartRefreshTriggers';
import { ToastListener } from './components/notifications/ToastListener';
import { ErrorDialogListener } from './components/notifications/ErrorDialogListener';
import { CartNoticeListener } from './components/notifications/CartNoticeListener';
import { GlobalErrorHandler } from './errors';
import { LazyMotion, domMax } from 'framer-motion';
import { initSentryAsync } from '@/monitoring/sentry';
import { DEFAULT_FREE_SHIPPING_THRESHOLD_CENTS } from './data/config';
// import { t } from './constants/i18n';

import { CartDrawer } from './components/cart/CartDrawer';

declare global {
    interface Window {
        toggleReactCart?: (open?: boolean) => void;
        __CART_INIT_SYNC_DONE__?: boolean;
    }
}

const FALLBACK_FREE_SHIPPING_THRESHOLD_CENTS = DEFAULT_FREE_SHIPPING_THRESHOLD_CENTS;

initSentryAsync('react-cart');

/**
 * 按优先级从 Liquid 注入的数据中读取免邮门槛，失败则兜底默认值。
 */
function bootstrapFreeShippingThreshold() {
    const applyThreshold = (value: unknown) => {
        const numeric = Number(value);
        if (Number.isFinite(numeric) && numeric > 0) {
            // 兜底：Liquid 注入通常是欧元，转换为 cents；若传入本身是 cents（>=1000），则直接使用
            const thresholdCents = numeric >= 1000 ? numeric : Math.round(numeric * 100);
            useCartStore.getState().setFreeShippingThreshold(thresholdCents);
            return true;
        }
        return false;
    };

    // 1) 优先读取 JSON 脚本节点（无转义，解析更稳）
    const jsonNode = document.getElementById('react-cart-data');
    if (jsonNode?.textContent) {
        try {
            const payload = JSON.parse(jsonNode.textContent);
            if (applyThreshold(payload?.freeShippingThreshold)) return;
        } catch (err) {
            console.warn('[Cart] Failed to parse react-cart-data', err);
        }
    }

    // 2) 回退读取 root 上的 data-cart-config（Liquid escape 过的字符串）
    const root = document.getElementById('react-cart-root');
    const configAttr = root?.getAttribute('data-cart-config');
    if (configAttr) {
        try {
            const payload = JSON.parse(configAttr);
            if (applyThreshold(payload?.freeShippingThreshold)) return;
        } catch (err) {
            console.warn('[Cart] Failed to parse data-cart-config', err);
        }
    }

    // 3) 最终兜底
    applyThreshold(FALLBACK_FREE_SHIPPING_THRESHOLD_CENTS);
}

/** Cart Island 根组件，聚合 Widget、Drawer、Toast/Error 监听 */
function CartIsland() {
    return (
        <LazyMotion features={domMax}>
            <div className="shopify-react-island">
                <div className="cart-island-container">
                    <ToastListener />
                    <CartNoticeListener />
                    <ErrorDialogListener />
                    <CartSideWidget className="top-[90%]" />
                    <CartDrawer />
                    {/* <div className="p-4 bg-white shadow rounded">
                        <h2>{t('cartIslandTitle')}</h2>
                        <p>{t('cartIslandDesc')}</p>
                    </div> */}
                </div>
            </div>
        </LazyMotion>
    );
}

CartEventBridge.init();
initNativeInterceptor();

if (typeof window !== 'undefined' && typeof window.toggleReactCart !== 'function') {
    window.toggleReactCart = (open = true) => {
        try {
            useCartStore.getState().toggleCart(open);
        } catch (error) {
            console.warn('[Cart] toggleReactCart failed', error);
        }
    };
}
bindHeaderCartLink();

function bindHeaderCartLink() {
    if (typeof window === 'undefined' || typeof window.toggleReactCart !== 'function') return;
    const cartLink = document.querySelector<HTMLAnchorElement>('#cartLink');
    if (!cartLink || cartLink.dataset.reactCartBound === 'true') return;

    cartLink.dataset.reactCartBound = 'true';
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    const openDrawer = () => window.toggleReactCart?.(true);

    cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        openDrawer();
    });
    cartLink.addEventListener('keydown', (event) => {
        const code = event.code.toUpperCase();
        if (code === 'SPACE' || code === 'ENTER') {
            event.preventDefault();
            openDrawer();
        }
    });
}

// 首屏同步购物车数据，并暴露刷新方法供桥接/拦截器调用
(window as any).refreshCartData = async (origin?: string) => {
    const isInit = origin === 'init';
    if (isInit && typeof window !== 'undefined') {
        if ((window as any).__CART_INIT_SYNC_DONE__) return;
        (window as any).__CART_INIT_SYNC_DONE__ = true;
    }
    try {
        const result = isInit
            ? await cartService.fetchCart()
            : await requestCartRefresh('manual');
        if (!result.ok) {
            GlobalErrorHandler.handle(result.error, {
                scope: isInit ? 'bootstrap' : 'refresh',
                source: 'fetch',
                userInitiated: false,
                silent: true
            });
        }
    } catch (error) {
        GlobalErrorHandler.handle(error, {
            scope: isInit ? 'bootstrap' : 'refresh',
            source: 'fetch',
            userInitiated: false,
            silent: true
        });
    }
};
void (window as any).refreshCartData('init');
initCartRefreshTriggers({ minIntervalMs: 2000 });
bootstrapFreeShippingThreshold();
mountReactRoot({
    rootId: 'react-cart-root',
    render: () => <CartIsland />
});

/** 
// 【调试专用】将核心对象暴露给 Window
console.log('🚀 Attempting to mount Debug Tools...'); // 加个日志确认代码走到了这里

(window as any).__DEBUG_CART__ = {
    service: cartService,
    store: useCartStore,
    errorHandler: GlobalErrorHandler,
    events: {
        toast: emitToast
    },
    getState: () => useCartStore.getState()
};

console.log('✅ Cart Debug Tools mounted at window.__DEBUG_CART__', (window as any).__DEBUG_CART__);
*/
